import React, { useEffect, useState, useRef, use, useMemo } from 'react'
import ReactApexChart from 'react-apexcharts'
import { IFText, IFSkeleton, ChartComponent } from 'Components'
import { Colors } from 'Theme'
import Styles from './ChargingSessionDetails.module.css'
import { useTranslation } from 'react-i18next'
import PropTypes from 'prop-types'
import RequestState from 'Enums/RequestState'
import moment from 'moment'
import { matchesDayTimeRestrictions } from 'Utils/dateTimeOperations'
import InfinityEnums from 'Enums/InfinityEnums'
import ReactTimeAgo from 'react-time-ago'

const ChargingSessionDetails = ({
  session,
  fetchChargingSessionRequestState,
  tariffsList,
  isLive = false,
  isSessionEnded,
  chargePointConnectors,
  operator,
}) => {
  const [tariff, setTariff] = useState(null)
  useEffect(() => {
    if (session?.connectorUid) {
      setTariff(tariffsList?.[0])
    }
    if (!tariffsList && session.connectorUid) {
      let connector = chargePointConnectors.find(
        (conn) => conn.uid === session.connectorUid,
      )
      setTariff(connector?.tariffIds?.[0])
    }
  }, [session, tariffsList, chargePointConnectors])

  const { t } = useTranslation()
  const chartRef = useRef(null)
  const seriesColors = [
    Colors.ConnectorStatus.CHARGING,
    Colors.ConnectorStatus.OCCUPIED,
    Colors.ConnectorStatus.SuspendedEVSE,
    Colors.ConnectorStatus.FAULTED,
    Colors.ConnectorStatus.FINISHING,
    Colors.ConnectorStatus.Empty,
  ]
  const energyDimensions = useMemo(() => {
    if (!session) return []
    return (
      session.chargingPeriods
        ?.flatMap((period) => period.dimensions || [])
        .filter((dim) => dim.type === InfinityEnums.CdrDimensionType.ENERGY) ||
      []
    )
  }, [session])

  const pieSeries = useMemo(() => {
    return energyDimensions.map((dim) => dim.volume)
  }, [energyDimensions])

  const pieLabels = useMemo(() => {
    if (energyDimensions.length === 0) {
      return ['Energy 1']
    }
    return energyDimensions.map((_, i) => `Energy ${i + 1}`)
  }, [energyDimensions])

  const pieSliceColors = useMemo(() => {
    return energyDimensions.map((dim, i) => {
      if (isLive && dim.volume < 0.1) {
        return Colors.ConnectorStatus.Empty
      }
      return seriesColors[i % seriesColors.length]
    })
  }, [energyDimensions, isLive])

  const [chartSeries, setChartSeries] = useState([])
  const [chartColors, setChartColors] = useState([Colors.ConnectorStatus.Empty])
  const [isEmptyOrZero, setIsEmptyOrZero] = useState(true)

  useEffect(() => {
    if (pieSeries.length === 0 || pieSeries.every((value) => value === 0)) {
      setIsEmptyOrZero(true)
      if (isLive) {
        setChartSeries([])
        setChartColors([Colors.ConnectorStatus.Empty])
      } else {
        setChartSeries([1])
        setChartColors([Colors.ConnectorStatus.Empty])
      }
    } else {
      setIsEmptyOrZero(false)
      setChartSeries([...pieSeries])
      setChartColors([...pieSliceColors])
    }
  }, [pieSeries, isLive])

  const [overridePower, setOverridePower] = useState(null)

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (chartRef.current && !chartRef.current.contains(event.target)) {
        setOverridePower(null)
      }
    }

    document.addEventListener('click', handleClickOutside)
    return () => {
      document.removeEventListener('click', handleClickOutside)
    }
  }, [])
  const pieOptions = useMemo(
    () => ({
      chart: {
        type: 'donut',
        animations: {
          enabled: true,
          easing: 'easeinout',
          speed: 800,
          animateGradually: {
            enabled: true,
            delay: 150,
          },
          dynamicAnimation: {
            enabled: true,
            speed: 350,
          },
        },
        events: {
          dataPointSelection: function (event, chartContext, config) {
            const index = config.dataPointIndex
            const value = config.w.globals.series[index]
            setOverridePower(value?.toFixed(3))
          },
        },
      },
      labels: pieLabels,
      legend: {
        show: false,
      },
      colors: chartColors,

      tooltip: {
        theme: 'dark',
        y: {
          formatter: (val) => {
            if (isEmptyOrZero) return '0.000 kWh'
            return `${val?.toFixed(3)} kWh`
          },
        },
      },
      dataLabels: {
        enabled: false,
      },
      stroke: {
        show: false,
      },
    }),
    [pieLabels, chartColors, chartSeries, isEmptyOrZero],
  )
  const startedAt = moment(session?.startedAt)
  const updatedAt = moment(session?.updatedAt)
  const [formattedDuration, setFormattedDuration] = useState()
  useEffect(() => {
    if (!isLive && updatedAt && startedAt) {
      const start = moment(startedAt)
      const end = moment(updatedAt)
      let durationMs = end.diff(start)

      if (durationMs < 10000) {
        const updatedEnd = Date.now()

        durationMs = moment(updatedEnd).diff(start)
      }

      const total = moment.duration(durationMs)
      const hours = Math.floor(total.asHours())
      const minutes = total.minutes()

      setFormattedDuration(`${hours}h ${minutes}m`)
    }
  }, [isLive, startedAt, updatedAt])

  const prices = session?.chargingPeriods?.map((period) => {
    const matchedTariff = tariff?.elements?.find((element) =>
      matchesDayTimeRestrictions(
        element?.restrictions || {},
        period?.startDateTime,
      ),
    )
    return matchedTariff?.priceComponents?.[0].price || 0
  })
  const avgTariff =
    prices?.reduce((sum, price) => sum + price, 0) / prices?.length

  const totalEnergy = session.chargingPeriods
    ?.flatMap((period) => period.dimensions || [])
    .filter((dim) => dim.type === InfinityEnums.CdrDimensionType.ENERGY)
    .reduce((sum, dim) => sum + (dim.volume || 0), 0)

  return (
    <div id="chart" className={Styles.WrapperContainer}>
      <div className={Styles.DetailsDiv}>
        <div className={Styles.MapHeader}>
          <IFText
            className={Styles.StatisticsSubTitle}
            style={{ color: Colors.text }}
            loadSkeleton={
              fetchChargingSessionRequestState === RequestState.LOADING
            }
            skeletonWidth={150}
            skeletonHeight={20}
          >
            {t('TransactionEvents.ChargingSessionDetails')}
          </IFText>
        </div>
        <div className={Styles.SessionDetailsDiv}>
          <div className={Styles.InfoDivLeft}>
            <IFText
              className={Styles.LabelText}
              loadSkeleton={
                fetchChargingSessionRequestState === RequestState.LOADING
              }
              skeletonWidth={150}
              skeletonHeight={20}
            >
              {t('TransactionEvents.TransactionId')}
            </IFText>
            <IFText
              className={Styles.DetailsText}
              loadSkeleton={
                fetchChargingSessionRequestState === RequestState.LOADING
              }
              skeletonWidth={110}
              skeletonHeight={20}
              style={{ color: Colors.text }}
            >
              {fetchChargingSessionRequestState === RequestState.SUCCEEDED &&
              session.transaction.uid
                ? session.transaction.uid
                : t('—')}
            </IFText>
          </div>
          <div className={Styles.InfoDivLeft}>
            <IFText
              className={Styles.LabelText}
              loadSkeleton={
                fetchChargingSessionRequestState === RequestState.LOADING
              }
              skeletonWidth={150}
              skeletonHeight={20}
            >
              {t('TransactionEvents.Connector')}
            </IFText>
            <IFText
              className={Styles.DetailsText}
              loadSkeleton={
                fetchChargingSessionRequestState === RequestState.LOADING
              }
              skeletonWidth={110}
              skeletonHeight={20}
              style={{ color: Colors.text }}
            >
              {fetchChargingSessionRequestState === RequestState.SUCCEEDED &&
              session?.connectorUid
                ? session.connectorUid
                : t('—')}
            </IFText>
          </div>
          <div className={Styles.InfoDivLeft}>
            <IFText
              className={Styles.LabelText}
              loadSkeleton={
                fetchChargingSessionRequestState === RequestState.LOADING
              }
              skeletonWidth={150}
              skeletonHeight={20}
            >
              {t('TransactionEvents.Started')}
            </IFText>
            <IFText
              className={Styles.DetailsText}
              loadSkeleton={
                fetchChargingSessionRequestState === RequestState.LOADING
              }
              skeletonWidth={110}
              skeletonHeight={20}
              style={{ color: Colors.text }}
            >
              {fetchChargingSessionRequestState === RequestState.SUCCEEDED &&
              session?.startedAt
                ? startedAt.format('YYYY-MM-DD HH:mm:ss')
                : t('—')}
            </IFText>
          </div>
          <div className={Styles.InfoDivLeft}>
            <IFText
              className={Styles.LabelText}
              loadSkeleton={
                fetchChargingSessionRequestState === RequestState.LOADING
              }
              skeletonWidth={150}
              skeletonHeight={20}
            >
              {t('TransactionEvents.Ended')}
            </IFText>
            <IFText
              className={Styles.DetailsText}
              loadSkeleton={
                fetchChargingSessionRequestState === RequestState.LOADING
              }
              skeletonWidth={110}
              skeletonHeight={20}
              style={{ color: Colors.text }}
            >
              {fetchChargingSessionRequestState === RequestState.SUCCEEDED &&
              session?.updatedAt &&
              isSessionEnded
                ? updatedAt.format('YYYY-MM-DD HH:mm:ss')
                : t('—')}
            </IFText>
          </div>
          <div className={Styles.InfoDivLeft}>
            <IFText
              className={Styles.LabelText}
              loadSkeleton={
                fetchChargingSessionRequestState === RequestState.LOADING
              }
              skeletonWidth={150}
              skeletonHeight={20}
            >
              {t('TransactionEvents.Duration')}
            </IFText>
            <IFText
              className={`${Styles.DetailsText} ${
                isLive ? Styles.livePulse : ''
              }`}
              style={isLive ? { color: Colors.primary, fontSize: 13 } : {}}
              loadSkeleton={
                fetchChargingSessionRequestState === RequestState.LOADING
              }
              skeletonWidth={110}
              skeletonHeight={20}
            >
              {isLive ? (
                <ReactTimeAgo
                  date={new Date(startedAt)}
                  locale="en-US"
                  timeStyle="mini"
                />
              ) : (
                <>{formattedDuration}</>
              )}
            </IFText>
          </div>
          <div className={Styles.InfoDivLeft}>
            <IFText
              className={Styles.LabelText}
              loadSkeleton={
                fetchChargingSessionRequestState === RequestState.LOADING
              }
              skeletonWidth={150}
              skeletonHeight={20}
            >
              {t('TransactionEvents.ChargingToken')}
            </IFText>
            <IFText
              className={Styles.DetailsText}
              loadSkeleton={
                fetchChargingSessionRequestState === RequestState.LOADING
              }
              skeletonWidth={110}
              skeletonHeight={20}
              style={{ color: Colors.text }}
            >
              {fetchChargingSessionRequestState === RequestState.SUCCEEDED &&
              session?.chargingToken
                ? session.chargingToken.huid
                : t('—')}
            </IFText>
          </div>
          <div className={Styles.InfoDivLeft}>
            <IFText
              className={Styles.LabelText}
              loadSkeleton={
                fetchChargingSessionRequestState === RequestState.LOADING
              }
              skeletonWidth={150}
              skeletonHeight={20}
            >
              {t('TransactionEvents.Reservation')}
            </IFText>
            <IFText
              className={Styles.DetailsText}
              loadSkeleton={
                fetchChargingSessionRequestState === RequestState.LOADING
              }
              skeletonWidth={110}
              skeletonHeight={20}
              style={{ color: Colors.text }}
            >
              {fetchChargingSessionRequestState === RequestState.SUCCEEDED &&
              session?.reservation
                ? session.reservation
                : t('—')}
            </IFText>
          </div>
        </div>
      </div>
      <div
        className={Styles.VerticalDivider}
        style={{
          backgroundColor: Colors.grey,
        }}
      />
      <div className={Styles.RightSessionDetailsDiv}>
        <div className={Styles.ChartDiv}>
          <div className={Styles.MapHeader}>
            <IFText
              className={Styles.StatisticsSubTitle}
              loadSkeleton={
                fetchChargingSessionRequestState === RequestState.LOADING
              }
              style={{ color: Colors.text }}
              skeletonWidth={150}
              skeletonHeight={20}
            >
              {t('TransactionEvents.TariffConsumption')}
            </IFText>
          </div>
          <div className={Styles.GraphDetailsContainer}>
            <div className={Styles.GraphContainer}>
              <div className={Styles.SessionDetailsDiv}>
                <div className={Styles.InfoDiv}>
                  <IFText
                    className={Styles.LabelText}
                    loadSkeleton={
                      fetchChargingSessionRequestState === RequestState.LOADING
                    }
                    skeletonWidth={130}
                    skeletonHeight={20}
                  >
                    {t('TransactionEvents.Tariff')}
                  </IFText>
                  <IFText
                    className={Styles.DetailsText}
                    loadSkeleton={
                      fetchChargingSessionRequestState === RequestState.LOADING
                    }
                    skeletonWidth={80}
                    skeletonHeight={20}
                    style={{ color: Colors.text }}
                  >
                    {fetchChargingSessionRequestState ===
                      RequestState.SUCCEEDED && session
                      ? tariff?.name
                      : t('—')}
                  </IFText>
                </div>
                <div className={Styles.InfoDiv}>
                  <IFText
                    className={Styles.LabelText}
                    loadSkeleton={
                      fetchChargingSessionRequestState === RequestState.LOADING
                    }
                    skeletonWidth={130}
                    skeletonHeight={20}
                  >
                    {t('TransactionEvents.TariffAvg')}
                    {`(${operator?.country?.currency.code || ''}/kWh)`}
                  </IFText>
                  <IFText
                    className={Styles.DetailsText}
                    loadSkeleton={
                      fetchChargingSessionRequestState === RequestState.LOADING
                    }
                    skeletonWidth={80}
                    skeletonHeight={20}
                    style={{ color: Colors.text }}
                  >
                    {fetchChargingSessionRequestState ===
                      RequestState.SUCCEEDED && !isNaN(avgTariff)
                      ? avgTariff
                      : t('—')}
                  </IFText>
                </div>
                <div className={Styles.InfoDiv}>
                  <IFText
                    className={Styles.LabelText}
                    loadSkeleton={
                      fetchChargingSessionRequestState === RequestState.LOADING
                    }
                    skeletonWidth={130}
                    skeletonHeight={20}
                  >
                    {t('TransactionEvents.TotalCost')}
                  </IFText>
                  <IFText
                    className={`${Styles.DetailsText} ${
                      isLive ? Styles.livePulse : ''
                    }`}
                    style={
                      isLive ? { color: Colors.primary, fontSize: 13 } : {}
                    }
                    loadSkeleton={
                      fetchChargingSessionRequestState === RequestState.LOADING
                    }
                    skeletonWidth={80}
                    skeletonHeight={20}
                  >
                    {fetchChargingSessionRequestState ===
                      RequestState.SUCCEEDED &&
                    session &&
                    session.totalCost
                      ? session.totalCost?.inclVat
                        ? session.totalCost.inclVat.toFixed(
                            session.chargePoint.station.operator.country
                              .currency.decimalPlaces || 2,
                          )
                        : session.totalCost?.exclVat?.toFixed(
                            session.chargePoint.station.operator.country
                              .currency.decimalPlaces || 2,
                          )
                      : t('—')}
                  </IFText>
                </div>
                <div className={Styles.InfoDiv}>
                  <IFText
                    className={Styles.LabelText}
                    loadSkeleton={
                      fetchChargingSessionRequestState === RequestState.LOADING
                    }
                    skeletonWidth={130}
                    skeletonHeight={20}
                  >
                    {t('TransactionEvents.TotalEnergy')}
                  </IFText>
                  <IFText
                    className={`${Styles.DetailsText} ${
                      isLive ? Styles.livePulse : ''
                    }`}
                    loadSkeleton={
                      fetchChargingSessionRequestState === RequestState.LOADING
                    }
                    skeletonWidth={80}
                    skeletonHeight={20}
                    style={
                      overridePower !== null || isLive
                        ? { color: Colors.primary, fontSize: 13 }
                        : {}
                    }
                  >
                    {fetchChargingSessionRequestState ===
                      RequestState.SUCCEEDED &&
                    overridePower !== null &&
                    session
                      ? overridePower
                      : totalEnergy?.toFixed(3) ?? t('—')}
                  </IFText>
                </div>
              </div>

              {fetchChargingSessionRequestState === RequestState.LOADING ? (
                <div className={Styles.ChartSkeleton}>
                  <IFSkeleton variant="circular" width={120} height={120} />
                </div>
              ) : (
                <>
                  <div className={Styles.GraphDiv} ref={chartRef}>
                    <ReactApexChart
                      key={`chart-${chartSeries.join('-')}`}
                      options={pieOptions}
                      series={chartSeries}
                      type="donut"
                      width={170}
                    />
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

ChargingSessionDetails.propTypes = {
  session: PropTypes.object,
  noData: PropTypes.bool,
  fetchChargingSessionRequestState: PropTypes.number,
}

export default ChargingSessionDetails
